import { SelectionModel } from '@angular/cdk/collections';
import { ChangeDetectorRef, ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';
import { MAT_LEGACY_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { MAT_LEGACY_TAB_GROUP } from '@angular/material/legacy-tabs';
import { MatLegacyTableDataSource, MatLegacyTableModule } from '@angular/material/legacy-table';

import { BehaviorSubject, Observable, Subject, Subscription, combineLatest } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, skip, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { MatLegacyInputModule } from '@angular/material/legacy-input';
import {
  ButtonComponent,
  ButtonLinkComponent,
  IconComponent,
  SearchInputComponent,
  TooltipDirective,
  trackByFactory,
} from '@mode/capra';
import { DatasetsFacade } from '@mode/dataset/domain';
import { ReportEditorFacade } from '@mode/report-editor/domain';
import { ReportFacade } from '@mode/reports/domain';
import {
  DatasetBrowserData,
  BrowserDatasetType,
  DatasetTabType,
  BrowserDatasetTabData,
} from '@mode/shared/contract-common';
import {
  DialogHeaderComponent,
  DialogService,
  PaginationViewComponent,
  TimeAgoPipe,
  StatusBannerComponent,
} from '@mode/shared/ui';
import { ReportCreationObservabilityService } from '@mode/shared/util-observability';
import { AnalyticsEvent, RunMethodType } from '@mode/shared/util-segment';
import { DataViewFacade } from '@mode/switch-views/contract';
import { DatasetSearchImportModalComponentFacade } from './dataset-search-import-modal.component.facade';
import { DatasetSearchDataTableComponent } from '../dataset-search-data-table/dataset-search-data-table.component';
import { getInitialFlagValue, IMPORT_DATASET_STATUS, isPresent } from '@mode/shared/util-js';
import { FeatureFlag } from '@mode/shared/contract-common';
import { DatasetSearchTabContentComponent } from '../dataset-search-tab-content/dataset-search-tab-content.component';
import { MatLegacyTabsModule } from '@angular/material/legacy-tabs';

const validDatasetUrl =
  /^(https?):\/\/[^/]*(\/editor)?\/\w*\/datasets\/(([a-f]|[0-9]){12})(\/queries\/([a-f]|[0-9]){12})?\/?$/;

@Component({
  standalone: true,
  imports: [
    ButtonComponent,
    ButtonLinkComponent,
    DialogHeaderComponent,
    CommonModule,
    MatLegacyInputModule,
    IconComponent,
    ReactiveFormsModule,
    TooltipDirective,
    TimeAgoPipe,
    PaginationViewComponent,
    DatasetSearchDataTableComponent,
    StatusBannerComponent,
    SearchInputComponent,
    DatasetSearchDataTableComponent,
    DatasetSearchTabContentComponent,
    MatLegacyTabsModule,
  ],
  providers: [DatasetSearchImportModalComponentFacade],
  selector: 'mode-dataset-search-import-modal',
  templateUrl: './dataset-search-import-modal.component.html',
  styleUrls: ['./dataset-search-import-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatasetSearchImportModalComponent implements OnInit, OnDestroy {
  workspace = this.createInitialTabData(['name', 'description', 'collection', 'creator', 'lastRefreshed']);
  personal = this.createInitialTabData(['name', 'description', 'lastRefreshed']);

  readonly tabs: DatasetTabType[] = [DatasetTabType.Workspace, DatasetTabType.Personal];
  readonly tabTrackByFn = trackByFactory<DatasetTabType>((tab) => tab);

  personalDataSource = new MatLegacyTableDataSource<DatasetBrowserData>();
  workspaceDataSource = new MatLegacyTableDataSource<DatasetBrowserData>();

  pageNum$ = new BehaviorSubject<number>(1);
  searchTerm$ = new Subject<string>();

  IMPORT_DATASET_STATUS = IMPORT_DATASET_STATUS;
  inputPlaceholder = `Paste a Dataset URL here, like https://app.mode.com/org/datasets/123`;

  errorMessage: string | undefined;
  accountId: string;
  reportId = '';
  selectedRow = new SelectionModel<DatasetBrowserData>(false, []);
  selectedDataset?: DatasetBrowserData;
  selectedTab = DatasetTabType.Workspace;
  newReport = true;
  datasetUrlControl: UntypedFormControl;
  subscriptions: Subscription[] = [];
  importDatasetResponse$: Observable<any>;
  destroyed$ = new Subject<void>();
  toolTipEnabled = false;
  isSampleDataset = false;
  toolTipHTML = '';
  enableDatasetsPagination = getInitialFlagValue(FeatureFlag.ShowDatasetsPagination);
  datasetTabType = DatasetTabType;
  constructor(
    @Inject(MAT_LEGACY_DIALOG_DATA) data: { org: string; reportId?: string; newReport: boolean },
    public datasetsFacade: DatasetsFacade,
    private dataViewFacade: DataViewFacade,
    private dialogService: DialogService,
    private reportEditorFacade: ReportEditorFacade,
    private reportFacade: ReportFacade,
    private datasetSearchImportModalComponentFacade: DatasetSearchImportModalComponentFacade,
    private o11yService: ReportCreationObservabilityService,
    private cdr: ChangeDetectorRef
  ) {
    this.accountId = data.org;
    this.reportId = data?.reportId ?? '';
    this.newReport = data.newReport;
    this.importDatasetResponse$ = this.reportEditorFacade.importDatasetResponse$;
    this.datasetUrlControl = new UntypedFormControl('', Validators.pattern(validDatasetUrl));
  }

  private createInitialTabData(columns: string[]): BrowserDatasetTabData {
    return {
      dataSources: [],
      pagination: { page: 0, per_page: 0, count: 0, total_pages: 0, total_count: 0 },
      searchEmptyState: false,
      searchTerm: '',
      isLoading: true,
      displayedColumns: columns,
    };
  }

  getTabName(tab: DatasetTabType) {
    if (tab === DatasetTabType.Workspace) {
      return this.enableDatasetsPagination ? 'Workspace' : 'Search';
    } else if (tab === DatasetTabType.Personal) {
      return this.enableDatasetsPagination ? 'My Personal' : 'By URL';
    }
    return tab;
  }

  setupSubscriptions() {
    this.subscriptions.push(
      this.pageNum$.pipe(skip(1), takeUntil(this.destroyed$)).subscribe((pageNum) => {
        const searchTerm =
          this.selectedTab === DatasetTabType.Workspace ? this.workspace.searchTerm : this.personal.searchTerm;
        this.loadDatasetData(pageNum, searchTerm, false);
      })
    );

    this.subscriptions.push(
      this.searchTerm$
        .pipe(skip(1), debounceTime(400), distinctUntilChanged(), takeUntil(this.destroyed$))
        .subscribe((searchTerm) => {
          this.loadDatasetData(1, searchTerm, true);
        })
    );

    this.subscriptions.push(
      combineLatest([
        this.datasetsFacade.personalSearchTerm$,
        this.datasetsFacade.workspaceSearchTerm$,
        this.datasetsFacade.personalDatasetPagination$,
        this.datasetsFacade.workspaceDatasetPagination$,
      ])
        .pipe(takeUntil(this.destroyed$))
        .subscribe(([personalTerm, workspaceTerm, personalPagination, workspacePagination]) => {
          this.personal = { ...this.personal, searchTerm: personalTerm, pagination: personalPagination };
          this.workspace = { ...this.workspace, searchTerm: workspaceTerm, pagination: workspacePagination };
        })
    );

    this.handleDatasetSubscription(this.datasetsFacade.workspaceDatasetBrowserData$, this.workspaceDataSource);

    this.handleDatasetSubscription(this.datasetsFacade.personalDatasetBrowserData$, this.personalDataSource);

    this.subscriptions.push(
      this.datasetUrlControl.valueChanges.pipe(debounceTime(100)).subscribe(() => {
        this.errorMessage = undefined;
      })
    );

    this.subscriptions.push(
      this.importDatasetResponse$.subscribe((response) => {
        if (response && response.status) {
          if (response.status === IMPORT_DATASET_STATUS.Success) {
            this.dialogService.closeAll();
          } else if (Object.values(IMPORT_DATASET_STATUS).includes(response.status)) {
            this.errorMessage = response.status;
          } else {
            this.errorMessage = IMPORT_DATASET_STATUS.Unknown;
          }
        }
      })
    );
  }

  private loadDatasetData(pageNum: number, searchTerm: string, isSearchTerm: boolean) {
    const payload = {
      org: this.accountId,
      datasetType:
        this.selectedTab === DatasetTabType.Workspace ? BrowserDatasetType.Workspace : BrowserDatasetType.Personal,
      pageNum,
      searchTerm,
      tabType: this.selectedTab,
    };
    this.datasetsFacade.loadDatasetBrowserData(payload, isSearchTerm);
  }

  private handleDatasetSubscription(
    browserData$: Observable<DatasetBrowserData[] | null>,
    dataSource: MatLegacyTableDataSource<DatasetBrowserData>
  ): void {
    this.subscriptions.push(
      browserData$
        .pipe(
          filter(isPresent),
          withLatestFrom(
            this.dataViewFacade.datasetResults$.pipe(
              map((datasetResult) => datasetResult.map((dataset) => dataset.datasetToken))
            )
          ),
          map(([browserData, alreadyImportedDatasets]) => {
            return (browserData || []).map((dataset) => ({
              ...dataset,
              alreadyImported: alreadyImportedDatasets.includes(dataset.datasetToken),
            }));
          })
        )
        .subscribe((data) => {
          dataSource.data = data;
          this.o11yService.endTrace();
          const targetTab = dataSource === this.workspaceDataSource ? this.workspace : this.personal;
          targetTab.dataSources = data;
          targetTab.isLoading = false;
          targetTab.searchEmptyState = data.length === 0 && targetTab.searchTerm !== '';
          this.cdr.detectChanges();
        })
    );
  }

  ngOnInit(): void {
    this.loadInitialData();
    this.setupSubscriptions();
    this.generateToolTipHTML();
  }

  loadInitialData() {
    const datasetConfigs = [
      { filter: BrowserDatasetType.Workspace, type: DatasetTabType.Workspace },
      { filter: BrowserDatasetType.Personal, type: DatasetTabType.Personal },
    ];

    datasetConfigs.forEach(({ filter, type }) => {
      const payload = {
        org: this.accountId,
        datasetType: filter,
        pageNum: 1,
        searchTerm: '',
        tabType: type,
      };
      this.datasetsFacade.loadDatasetBrowserData(payload);
    });
  }

  generateToolTipHTML() {
    this.toolTipHTML = `<p style="text-align: center; margin-top: 4px; font-family: Graphik">
    Previewing isn't supported for Mode's sample Datasets</p>`;
  }

  onEnter() {
    if (!this.errorMessage) {
      this.importDatasetFromUrl(this.datasetUrlControl.value);
    }
  }

  closeDialog() {
    this.dialogService.closeAll();
  }

  importDatasetFromSearch() {
    if (this.selectedDataset) {
      if (this.newReport) {
        this.getDataset(this.selectedDataset.datasetToken, true, this.selectedDataset.accountId ?? 'ERROR');
      } else {
        const accountId = this.selectedDataset.isSampleDataset ? 'datasetters' : this.accountId;
        const url = `${window.location.origin}/editor/${accountId}/datasets/${this.selectedDataset.datasetToken}`;
        this.getDatasetForExistingReport(this.selectedDataset.datasetToken, true, url, this.reportId, accountId);
      }
      this.reportFacade.submitUseDatasetAnalytics(
        RunMethodType.Track,
        AnalyticsEvent.Name.UiClicked,
        AnalyticsEvent.SubContext.DatasetBrowser,
        'use dataset',
        AnalyticsEvent.Context.Report,
        this.reportId
      );
    }
  }

  importDatasetFromUrl(url: string) {
    const urlArray = url.match(validDatasetUrl);
    const datasetToken = urlArray?.[3] ?? '';
    const accountId = url.includes('datasetters') ? 'datasetters' : this.accountId;

    if (this.newReport) {
      this.getDataset(datasetToken, false, accountId);
    } else {
      this.getDatasetForExistingReport(datasetToken, false, url, this.reportId, accountId);
    }
  }

  private getDataset(datasetToken: string, isSelectedDataset: boolean, accountId: string) {
    this.dataViewFacade
      .getDatasetWithSwitchViewToken(accountId, datasetToken)
      .pipe(take(1))
      .subscribe((dataset) => {
        this.datasetSearchImportModalComponentFacade.importDataset(dataset, isSelectedDataset);
      });
  }

  private getDatasetForExistingReport(
    datasetToken: string,
    isSelectedDataset: boolean,
    queryToken: string,
    reportId: string,
    accountId: string
  ) {
    this.dataViewFacade
      .getDatasetWithSwitchViewToken(accountId, datasetToken)
      .pipe(take(1))
      .subscribe((dataset) => {
        this.datasetSearchImportModalComponentFacade.importDatasetExistingReport(
          dataset,
          isSelectedDataset,
          queryToken,
          reportId
        );
      });
  }

  setTab(tabName: DatasetTabType) {
    this.selectedTab = tabName;
    this.isSampleDataset = false;
    this.selectedDataset = undefined;
  }

  selectDataset(element: DatasetBrowserData) {
    if (element.isSampleDataset) {
      this.isSampleDataset = true;
      element.accountId = 'datasetters';
    } else {
      this.isSampleDataset = false;
      element.accountId = this.accountId;
    }
    this.selectedRow.toggle(element);
    this.selectedDataset = element;
  }

  filterResults(searchTerm: string) {
    this.searchTerm$.next(searchTerm);
  }

  enableToolTipCheck(e: any) {
    if (e.target.offsetWidth < e.target.scrollWidth) {
      this.toolTipEnabled = true;
    }
  }

  hideToolTip() {
    this.toolTipEnabled = false;
  }

  ngOnDestroy() {
    this.reportEditorFacade.clearImportDatasetResponse();
    this.destroyed$.next();
    this.destroyed$.complete();
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  handleChangeCurrentPage(pageNum: number) {
    this.pageNum$.next(pageNum);
  }
}
